/**
 * Home enrichment lifecycle derivations (guide §5, §8, §9).
 *
 * Every Home enrichment surface — Needs Attention, Enrichment & AI and the two
 * KPI tiles — is derived here from ONE list of normalized executions, so the
 * KPI values and the widget rows can never drift apart (guide §4, §22).
 *
 * The list comes from the `loadEnrichmentExecutions` slot of the Home data
 * adapter. A backend feed maps its rows into `normalizeHomeExecution` input;
 * nothing in this module knows where the rows came from.
 */

export const HOME_ENRICHMENT_WIDGET_LIMIT = 5;

export const HOME_EXECUTION_KIND_DATA = "data-enrichment";
export const HOME_EXECUTION_KIND_AI = "ai-agent";

const STATUSES = new Set(["running", "failed", "completed"]);
const ORIGINS = new Set(["workspace", "search", "chat"]);

/** Nav-state keys shared with the origin pages' restore path (guide §5). */
export const ENRICHMENT_REVIEW_FOCUS_STATE = "enrichmentAttentionFocus";

function toTimestamp(value) {
  if (value == null || value === "") return null;
  const ms = typeof value === "number" ? value : new Date(value).getTime();
  return Number.isFinite(ms) ? ms : null;
}

function toCount(value) {
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? number : null;
}

function resolveKind(raw) {
  const kind = String(raw.kind || raw.actionType || raw.actionKey || "");
  return kind === HOME_EXECUTION_KIND_AI || kind.startsWith("ai_enrichment")
    ? HOME_EXECUTION_KIND_AI
    : HOME_EXECUTION_KIND_DATA;
}

/**
 * Normalize one execution. Returns null for rows without an id or with an
 * unknown status — Home never guesses a lifecycle state.
 */
export function normalizeHomeExecution(raw) {
  if (!raw || typeof raw !== "object") return null;
  const id = String(raw.id || "").trim();
  const status = String(raw.status || "").trim();
  if (!id || !STATUSES.has(status)) return null;

  const kind = resolveKind(raw);
  const workspaceId = raw.workspaceId ? String(raw.workspaceId) : null;
  const origin = ORIGINS.has(raw.origin) ? raw.origin : workspaceId ? "workspace" : "search";
  const attempted = toCount(raw.attempted);
  const successful = toCount(raw.successful) ?? 0;
  const noResult = toCount(raw.noResult) ?? (attempted != null ? Math.max(0, attempted - successful) : 0);
  const startedAt = toTimestamp(raw.startedAt);
  const completedAt = toTimestamp(raw.completedAt);

  return {
    id,
    kind,
    status,
    title: String(raw.title || "").trim() || (kind === HOME_EXECUTION_KIND_AI ? "AI Agent" : "Data Enrichment"),
    actionKey: raw.actionKey ? String(raw.actionKey) : null,
    leftOrigin: raw.leftOrigin === true,
    origin,
    originPath: raw.originPath ? String(raw.originPath) : null,
    workspaceId,
    sessionId: raw.sessionId ? String(raw.sessionId) : null,
    attempted: attempted ?? successful + noResult,
    successful,
    noResult,
    startedAt,
    completedAt,
    lastInteractionAt: toTimestamp(raw.lastInteractionAt) ?? completedAt ?? startedAt,
  };
}

export function normalizeHomeExecutions(rows) {
  return (Array.isArray(rows) ? rows : []).map(normalizeHomeExecution).filter(Boolean);
}

/**
 * R1 / guide §5: executions the user LEFT before completion that are still
 * running or failed. Running on the origin page, and every completed run
 * (including 0 results), are excluded.
 */
export function selectNeedsAttention(executions) {
  return executions
    .filter((row) => row.leftOrigin && (row.status === "running" || row.status === "failed"))
    .sort((a, b) => (b.lastInteractionAt || 0) - (a.lastInteractionAt || 0));
}

/** Full completed history, newest completion first (guide §8). */
export function selectCompletedHistory(executions) {
  return executions
    .filter((row) => row.status === "completed")
    .sort((a, b) => (b.completedAt || 0) - (a.completedAt || 0));
}

/**
 * One derivation for the three enrichment surfaces. KPI counts are the
 * lengths of the same arrays the widgets render from.
 */
export function deriveHomeEnrichment(rows, { completedTotal = null } = {}) {
  const executions = normalizeHomeExecutions(rows);
  const attentionItems = selectNeedsAttention(executions);
  const history = selectCompletedHistory(executions);
  return {
    attentionItems,
    needsAttentionCount: attentionItems.length,
    completedItems: history.slice(0, HOME_ENRICHMENT_WIDGET_LIMIT),
    // A server total (all-time) wins over the rows, which hold only the newest.
    completedTotal: Number.isFinite(completedTotal) ? Math.max(completedTotal, history.length) : history.length,
    history,
  };
}

/** Review results → exact origin (guide §5). Never a new run. */
export function resolveExecutionPath(item) {
  if (item?.originPath) return item.originPath;
  if (item?.workspaceId) return `/workspaces/${item.workspaceId}`;
  return "/lists/companies/";
}

/**
 * Navigation state for Review results. Carries the SAME execution id so the
 * origin page can restore it. Only Needs Attention passes `markReviewed`: the
 * origin page then marks that run reviewed server-side (G8). Enrichment & AI
 * and Continue working never do (Spec §12 #17, #21).
 */
export function buildExecutionReviewState(item, { markReviewed = false } = {}) {
  if (!item?.id) return null;
  return {
    [ENRICHMENT_REVIEW_FOCUS_STATE]: true,
    focusEnrichmentColumn: item.actionKey || item.title || undefined,
    enrichmentAttentionId: item.id,
    ...(markReviewed ? { markReviewed: true } : {}),
  };
}
